import {
  Calculator,
  FileSignature,
  PencilRuler,
  Boxes,
  BrainCircuit,
  Crown,
  UserCheck,
  Wrench,
  Package,
  ClipboardList,
  FileSpreadsheet,
  HardHat,
  Target,
  SprayCan,
  Construction,
  Building2,
} from 'lucide-react';

/**
 * Canonical department → icon + color + shortName mapping.
 *
 * Keys are **exact** department names as stored in the database.
 * Every department gets a unique Tailwind color family so they are
 * visually distinguishable across the portal (accordion cards, charts,
 * dashboard analytics, etc.).
 *
 * `shortName` is the dense-chip label for tables/filters (e.g. HR, DTO, QS).
 * Full `department_name` remains the legal/filter identity — always expose
 * it via tooltip (`title`) when rendering the short form.
 *
 * Color shape:
 *   bg      – light background for icon containers  (e.g. bg-emerald-100)
 *   text    – icon / label foreground                (e.g. text-emerald-600)
 *   solid   – solid bar / dot color                  (e.g. bg-emerald-500)
 *   hex     – hex value for Recharts / canvas        (e.g. #10b981)
 *   ring    – focus-ring color                       (e.g. ring-emerald-300)
 */
const DEPARTMENT_CONFIG = {
  'Accounts': {
    shortName: 'Accounts',
    icon: Calculator,
    color: {
      bg: 'bg-emerald-100',
      text: 'text-emerald-600',
      solid: 'bg-emerald-500',
      hex: '#10b981',
      ring: 'ring-emerald-300',
    },
  },
  'Contracts': {
    shortName: 'Contracts',
    icon: FileSignature,
    color: {
      bg: 'bg-indigo-100',
      text: 'text-indigo-600',
      solid: 'bg-indigo-500',
      hex: '#6366f1',
      ring: 'ring-indigo-300',
    },
  },
  'Design & BIM': {
    shortName: 'Design & BIM',
    icon: PencilRuler,
    color: {
      bg: 'bg-pink-100',
      text: 'text-pink-600',
      solid: 'bg-pink-500',
      hex: '#ec4899',
      ring: 'ring-pink-300',
    },
  },
  'DfMA': {
    shortName: 'DfMA',
    icon: Boxes,
    color: {
      bg: 'bg-amber-100',
      text: 'text-amber-600',
      solid: 'bg-amber-500',
      hex: '#f59e0b',
      ring: 'ring-amber-300',
    },
  },
  'Digital Transformation Office': {
    shortName: 'DTO',
    icon: BrainCircuit,
    color: {
      bg: 'bg-cyan-100',
      text: 'text-cyan-600',
      solid: 'bg-cyan-500',
      hex: '#06b6d4',
      ring: 'ring-cyan-300',
    },
  },
  'Director': {
    shortName: 'Director',
    icon: Crown,
    color: {
      bg: 'bg-slate-100',
      text: 'text-slate-600',
      solid: 'bg-slate-500',
      hex: '#64748b',
      ring: 'ring-slate-300',
    },
  },
  'Human Resources': {
    shortName: 'HR',
    icon: UserCheck,
    color: {
      bg: 'bg-purple-100',
      text: 'text-purple-600',
      solid: 'bg-purple-500',
      hex: '#a855f7',
      ring: 'ring-purple-300',
    },
  },
  'Operations': {
    shortName: 'Ops',
    icon: Wrench,
    color: {
      bg: 'bg-orange-100',
      text: 'text-orange-600',
      solid: 'bg-orange-500',
      hex: '#f97316',
      ring: 'ring-orange-300',
    },
  },
  'Procurement': {
    shortName: 'Proc',
    icon: Package,
    color: {
      bg: 'bg-teal-100',
      text: 'text-teal-600',
      solid: 'bg-teal-500',
      hex: '#14b8a6',
      ring: 'ring-teal-300',
    },
  },
  'Project': {
    shortName: 'Project',
    icon: ClipboardList,
    color: {
      bg: 'bg-blue-100',
      text: 'text-blue-600',
      solid: 'bg-blue-500',
      hex: '#3b82f6',
      ring: 'ring-blue-300',
    },
  },
  'Quantity Surveyor': {
    shortName: 'QS',
    icon: FileSpreadsheet,
    color: {
      bg: 'bg-lime-100',
      text: 'text-lime-600',
      solid: 'bg-lime-500',
      hex: '#84cc16',
      ring: 'ring-lime-300',
    },
  },
  'Safety': {
    shortName: 'Safety',
    icon: HardHat,
    color: {
      bg: 'bg-red-100',
      text: 'text-red-600',
      solid: 'bg-red-500',
      hex: '#ef4444',
      ring: 'ring-red-300',
    },
  },
  'Strategic': {
    shortName: 'Strategic',
    icon: Target,
    color: {
      bg: 'bg-violet-100',
      text: 'text-violet-600',
      solid: 'bg-violet-500',
      hex: '#8b5cf6',
      ring: 'ring-violet-300',
    },
  },
  'Support': {
    shortName: 'Support',
    icon: SprayCan,
    color: {
      bg: 'bg-sky-100',
      text: 'text-sky-600',
      solid: 'bg-sky-500',
      hex: '#0ea5e9',
      ring: 'ring-sky-300',
    },
  },
  'Teams': {
    shortName: 'Teams',
    icon: Construction,
    color: {
      bg: 'bg-rose-100',
      text: 'text-rose-600',
      solid: 'bg-rose-500',
      hex: '#f43f5e',
      ring: 'ring-rose-300',
    },
  },
};

const FALLBACK = {
  shortName: null,
  icon: Building2,
  color: {
    bg: 'bg-gray-100',
    text: 'text-gray-600',
    solid: 'bg-gray-500',
    hex: '#6b7280',
    ring: 'ring-gray-300',
  },
};

export function getDepartmentConfig(deptName) {
  if (!deptName) return FALLBACK;
  return DEPARTMENT_CONFIG[deptName] || FALLBACK;
}

export function getDepartmentIcon(deptName) {
  return getDepartmentConfig(deptName).icon;
}

export function getDepartmentColor(deptName) {
  return getDepartmentConfig(deptName).color;
}

/**
 * Dense-chip short label for tables (e.g. HR, DTO, QS).
 * Falls back to the full department name when no short form is configured.
 *
 * Always pair with `title={fullDepartmentName}` on the chip so HR can
 * confirm identity on hover.
 */
export function getDepartmentShortName(deptName) {
  if (!deptName) return '';
  const cfg = DEPARTMENT_CONFIG[deptName];
  return cfg?.shortName || deptName;
}

/**
 * Returns colors shaped for the dashboard analytics cards / table:
 *   bg    – solid bar / dot color   (e.g. bg-emerald-500)
 *   light – light badge background  (e.g. bg-emerald-50)
 *   text  – label text              (e.g. text-emerald-700)
 */
export function getDepartmentDashboardColor(deptName) {
  const c = getDepartmentConfig(deptName).color;
  return {
    bg: c.solid,
    light: c.bg,
    text: c.text.replace('-600', '-700'),
  };
}

/**
 * Returns a hex color for the given department name.
 * Useful for Recharts / canvas / inline-style contexts.
 */
export function getDepartmentHex(deptName) {
  return getDepartmentConfig(deptName).color.hex;
}

/**
 * Returns the InfoBadge-compatible color key (e.g. 'emerald', 'indigo').
 * Extracted from the solid class: 'bg-emerald-500' → 'emerald'.
 */
export function getDepartmentBadgeColor(deptName) {
  return getDepartmentConfig(deptName).color.solid.split('-')[1];
}

/**
 * Returns an ordered array of { name, hex } for all configured departments.
 * Useful for chart color scales that iterate over departments.
 */
export function getAllDepartmentHexes() {
  return Object.entries(DEPARTMENT_CONFIG).map(([name, cfg]) => ({
    name,
    hex: cfg.color.hex,
  }));
}

export default DEPARTMENT_CONFIG;
